import { Order, TeacherOrder } from './orderService';

// ========== 状态配置 ==========
export const ORDER_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  pending: { label: '待支付', color: 'text-yellow-700', bgColor: 'bg-yellow-100' },
  paid: { label: '已支付', color: 'text-green-700', bgColor: 'bg-green-100' },
  expired: { label: '已关闭', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  cancelled: { label: '已关闭', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  refunded: { label: '已退款', color: 'text-red-700', bgColor: 'bg-red-100' },
};

export function getStatusConfig(status: string) {
  return ORDER_STATUS_CONFIG[status] || { label: status || '未知', color: 'text-gray-700', bgColor: 'bg-gray-100' };
}

// ========== 支付方式 ==========

/**
 * 根据订单的实际支付特征组合显示支付方式
 * 规则：U币抵扣、支付宝支付、券码兑换、后台开通，多种组合用"+"连接
 */
export function getPayMethodLabel(order: Pick<Order, 'payMethod' | 'pointsDeducted' | 'actualPrice' | 'source'>): string {
  const methods: string[] = [];

  // 1. 有U币抵扣 → "U币"
  if ((order.pointsDeducted || 0) > 0) {
    methods.push('U币');
  }

  // 2. 判断是否有支付宝/微信支付
  const payMethod = order.payMethod || '';
  if (payMethod === 'alipay' || payMethod === 'points_alipay') {
    methods.push('支付宝');
  } else if (payMethod === 'wechat') {
    methods.push('微信');
  } else if (payMethod === 'coupon') {
    methods.push('券码');
  } else if (payMethod === 'admin') {
    methods.push('后台');
  } else if (payMethod === 'points') {
    // 纯U币支付，上面已经加了"U币"，不重复
  } else if (!payMethod && (order.pointsDeducted || 0) === 0) {
    // payMethod 为空且没有U币抵扣，兼容旧数据
    if ((order.actualPrice ?? 0) > 0) {
      methods.push('支付宝');
    }
  }

  if (methods.length > 0) {
    return methods.join('+');
  }

  return payMethod || '-';
}

// ========== 套餐类型 ==========
export const DURATION_LABELS: Record<string, string> = {
  month: '月包',
  quarter: '季包',
  halfYear: '半年包',
  year: '年包',
  custom: '自定义包',
};

export function getDurationLabel(duration: string): string {
  return DURATION_LABELS[duration] || duration || '-';
}

// ========== 金额计算 ==========

/**
 * 统一计算实付金额
 * 优先使用 actualPrice，否则用 originalPrice - deductionAmount
 */
export function calcActualPrice(order: Pick<Order, 'price' | 'originalPrice' | 'actualPrice' | 'deductionAmount'>): number {
  if (order.actualPrice != null) {
    return order.actualPrice;
  }
  const base = order.originalPrice ?? order.price ?? 0;
  return base - (order.deductionAmount || 0);
}

/**
 * 获取订单总额（原价）
 */
export function calcOriginalPrice(order: Pick<Order, 'price' | 'originalPrice'>): number {
  return order.originalPrice ?? order.price ?? 0;
}

// ========== 渠道来源 ==========
export const SOURCE_LABELS: Record<string, string> = {
  self: '自购',
  admin: '后台',
  coupon_redeem: '自购',
};

export function getSourceLabel(source: string): string {
  return SOURCE_LABELS[source] || source || '-';
}

// ========== 教师订单专用 ==========

export const TEACHER_PACKAGE_TYPE_LABELS: Record<string, string> = {
  month: '月包',
  quarter: '季包',
  halfYear: '半年包',
  year: '年包',
  custom: '自定义包',
};

export function getTeacherPackageTypeLabel(packageType: string): string {
  return TEACHER_PACKAGE_TYPE_LABELS[packageType] || packageType || '-';
}

/**
 * 获取教师套餐纯名称（不含类型和人数）
 */
export function getTeacherPackageName(order: Pick<TeacherOrder, 'orgName' | 'catName' | 'levelText'>): string {
  return [order.orgName, order.catName, order.levelText].filter(Boolean).join(' / ') || '-';
}

/**
 * 获取教师套餐信息展示文本（含类型和人数，用于导出等场景）
 * 格式：机构 / 类别 / 级别 · 类型 · 人数
 */
export function getTeacherPackageLabel(order: Pick<TeacherOrder, 'orgName' | 'catName' | 'levelText' | 'packageType' | 'peopleCount'>): string {
  const base = getTeacherPackageName(order);
  const typeStr = getTeacherPackageTypeLabel(order.packageType);
  const peopleStr = order.peopleCount ? `${order.peopleCount}人` : '';
  return [base, typeStr, peopleStr].filter(Boolean).join(' · ');
}

/**
 * 教师订单实付金额计算（复用通用逻辑，适配 TeacherOrder 字段）
 */
export function calcTeacherActualPrice(order: Pick<TeacherOrder, 'price' | 'originalPrice' | 'actualPrice' | 'deductionAmount'>): number {
  if (order.actualPrice != null) {
    return order.actualPrice;
  }
  const base = order.originalPrice ?? order.price ?? 0;
  return base - (order.deductionAmount || 0);
}

export function calcTeacherOriginalPrice(order: Pick<TeacherOrder, 'price' | 'originalPrice'>): number {
  return order.originalPrice ?? order.price ?? 0;
}
